import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from '@doccareplus/i18n';
import type {
  PrescriptionBrandingResponse,
  PrescriptionPatientHeader,
  PrescriptionPageSize,
  PrintMode,
} from './types';
import { resolvePrescriptionPageDimensions } from './pageSize';

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString();

/**
 * PrescriptionPaper — the shared paged-paper shell used by the editable visit workspace, the read-only finalized
 * record, the prescription desk print view, and the clinic settings live preview.
 *
 * Every letterhead value comes from `branding`, resolved server-side from the clinic record and the clinic's print
 * settings. Visibility toggles are already applied there, so a field that arrives absent was deliberately hidden or
 * never entered: it renders as omitted. Nothing here substitutes a placeholder or sample value — a plausible-looking
 * fake clinic address on a clinical document would be worse than a blank.
 *
 * `PrePrintedStationery` hides the rendered letterhead and footer branding while keeping the reserved space, so
 * content lands clear of the clinic's pre-printed paper.
 *
 * See .ai/BusinessDomain/visit-prescription.md → "Letterhead Resolution".
 */
export function PrescriptionPaper({
  branding,
  printMode,
  pageSize,
  paged = false,
  providerName,
  providerQualification,
  providerRegNo,
  patient,
  visitDate,
  allergy,
  shareUrl,
  clinicFallbackName,
  children,
}: {
  branding: PrescriptionBrandingResponse | undefined;
  /** Overrides the clinic default for this render only (a per-print choice). */
  printMode?: PrintMode;
  pageSize?: PrescriptionPageSize;
  /**
   * Renders at the selected page proportions with configured reserved space in real millimetres. Used by the print
   * view and the settings preview; the editable workspace keeps fluid padding so the form stays comfortable.
   */
  paged?: boolean;
  providerName: string;
  providerQualification?: string | null;
  providerRegNo?: string | null;
  patient: PrescriptionPatientHeader;
  visitDate: string;
  allergy?: string | null;
  /** Guest share-link URL, when one was minted for this print/preview — rendered as a scannable QR. */
  shareUrl?: string | null;
  /** Used only when an older payload has no resolved branding object. */
  clinicFallbackName?: string | null;
  children: React.ReactNode;
}) {
  const { t } = useTranslation('appointments');

  const mode = printMode ?? branding?.defaultPrintMode ?? 'DigitalLetterhead';
  const preprinted = mode === 'PrePrintedStationery';
  const accent = branding?.accentColor ?? 'var(--primary)';
  const { widthMm, heightMm } = resolvePrescriptionPageDimensions(branding, pageSize);

  const clinicSubtitle = [branding?.addressLine, branding?.phone, branding?.email]
    .filter(Boolean)
    .join(' · ');

  const meta = [
    patient.age != null ? t('visit.header.years', { count: patient.age }) : null,
    // The raw "Unspecified" enum value reads as a data error on a printed clinical document, not an
    // intentional omission — treat it the same as gender never being recorded.
    patient.gender && patient.gender !== 'Unspecified' ? patient.gender : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const footerLines = branding?.footerLines ?? [];
  const hasFooter =
    !preprinted &&
    (footerLines.length > 0 ||
      Boolean(branding?.showDigitallySignedNotice) ||
      Boolean(branding?.showPageNumber) ||
      Boolean(shareUrl));

  const reservedSpace = {
    paddingTop: `${branding?.topMarginMm ?? 20}mm`,
    paddingBottom: `${branding?.bottomMarginMm ?? 15}mm`,
    paddingLeft: `${branding?.leftMarginMm ?? 15}mm`,
    paddingRight: `${branding?.rightMarginMm ?? 15}mm`,
  };

  return (
    <div
      data-testid={paged ? 'rx-document' : undefined}
      className={`rx-paper mx-auto overflow-hidden rounded-[6px] bg-surface shadow-[var(--shadow-2)] ${
        paged ? 'max-w-none' : 'w-full max-w-[824px]'
      }`}
      style={
        {
          // Consumed by the @media print rules in the design-system stylesheet, so the clinic's configured
          // millimetres are what actually reach the printed paper.
          '--rx-margin-top': `${branding?.topMarginMm ?? 20}mm`,
          '--rx-margin-bottom': `${branding?.bottomMarginMm ?? 15}mm`,
          '--rx-margin-left': `${branding?.leftMarginMm ?? 15}mm`,
          '--rx-margin-right': `${branding?.rightMarginMm ?? 15}mm`,
          ...(paged ? { width: `${widthMm}mm`, minHeight: `${heightMm}mm` } : {}),
        } as React.CSSProperties
      }
    >
      {!preprinted && <div className="h-[5px] w-full print:hidden" style={{ background: accent }} />}

      {/*
        The three groups below become an actual CSS table at print time (`print:table` / `table-*-group`):
        browsers repeat `table-header-group`/`table-footer-group` content on every printed page automatically,
        which is the standard cross-browser way to get a repeating letterhead/footer on a multi-page prescription
        without measuring pixel heights in JS. On screen this stays a plain flex column.
      */}
      <div
        className="rx-paper-content flex flex-col gap-6 print:table print:w-full"
        style={paged ? { ...reservedSpace, minHeight: `calc(${heightMm}mm - 5px)` } : { padding: 'clamp(22px,4vw,52px)' }}
      >
        <div className="print:table-header-group">
          {!preprinted && (
            <header
              data-testid="rx-letterhead"
              className="rx-letterhead rx-no-break flex flex-wrap items-start justify-between gap-4 border-b-2 pb-4 print:mb-6"
              style={{ borderColor: accent }}
            >
              <div className="flex items-center gap-3">
                {branding?.logoUrl ? (
                  <img
                    data-testid="rx-logo-image"
                    src={branding.logoUrl}
                    alt=""
                    className="h-10 w-10 flex-none rounded-[10px] object-contain"
                  />
                ) : (
                  <div
                    data-testid="rx-logo-fallback"
                    aria-hidden="true"
                    className="flex h-10 w-10 flex-none items-center justify-center rounded-[10px] text-white"
                    style={{ backgroundColor: accent }}
                  >
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 21h18" />
                      <path d="M5 21V7l7-4 7 4v14" />
                      <path d="M9 21v-6h6v6" />
                    </svg>
                  </div>
                )}
                <div>
                  {(branding?.clinicName || (!branding && clinicFallbackName)) && (
                    <div className="text-18 font-extrabold" style={{ color: accent }}>
                      {branding?.clinicName || clinicFallbackName}
                    </div>
                  )}
                  {branding?.headerSubtitle && (
                    <div className="text-12 text-text-2">{branding.headerSubtitle}</div>
                  )}
                  {clinicSubtitle && <div className="text-11 text-text-3">{clinicSubtitle}</div>}
                  {branding?.registrationNumber && (
                    <div className="font-mono text-11 text-text-3">
                      {t('visit.details.regNo', { value: branding.registrationNumber })}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right">
                {providerName && <div className="text-13 font-bold text-text">{providerName}</div>}
                {/* Omitted when the provider never recorded it — never a stand-in qualification. */}
                {providerQualification && <div className="text-11 text-text-3">{providerQualification}</div>}
                {providerRegNo && (
                  <div className="font-mono text-11 text-text-3">
                    {t('visit.details.regNo', { value: providerRegNo })}
                  </div>
                )}
              </div>
            </header>
          )}
        </div>

        <div className="flex flex-col gap-6 print:table-row-group">
          {/* Patient identity line */}
          <div className="rx-no-break -mt-2 flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <span className="text-17 font-bold text-text">{patient.fullName}</span>
              {meta && <span className="ml-2 text-13 text-text-3">{meta}</span>}
            </div>
            <div className="text-right text-12 text-text-3">
              <div className="font-mono">{patient.patientNumber}</div>
              <div>{fmtDate(visitDate)}</div>
            </div>
          </div>

          {allergy && (
            <div className="rx-no-break -mt-3 inline-flex w-fit items-center gap-2 rounded-[6px] border border-danger bg-danger-bg px-3 py-1.5 text-12 font-bold text-danger">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.3} aria-hidden>
                <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
              </svg>
              {t('visit.header.allergy')} — {allergy}
            </div>
          )}

          {children}
        </div>

        {hasFooter && (
          <div className="mt-auto print:table-footer-group">
            <footer
              data-testid="rx-footer"
              className="rx-page-footer rx-no-break border-t border-border pt-3 text-11 text-text-3 print:mt-6"
            >
              {footerLines.map((line) => (
                <div key={line}>{line}</div>
              ))}
              <div className="flex items-center justify-between gap-3">
                {branding?.showDigitallySignedNotice && <span>{t('visit.details.digitallySigned')}</span>}
                {branding?.showPageNumber && (
                  <span className="ml-auto">
                    {t('visit.details.page')} <span className="rx-page-number" />
                  </span>
                )}
              </div>
              {shareUrl && (
                <div className="rx-no-break mt-2 flex items-center justify-between gap-3 border-t border-border pt-2">
                  <div className="flex items-center gap-1.5">
                    <img src="/short_logo.svg" alt="" className="h-4 w-4 flex-none" />
                    <span className="text-10 font-bold tracking-wide text-text-2">DocCare+</span>
                  </div>
                  <div className="flex items-center gap-2 text-text-2">
                    <span className="text-10">{t('visit.details.scanToView')}</span>
                    <QRCodeSVG
                      value={shareUrl}
                      size={44}
                      level="M"
                      bgColor="transparent"
                      fgColor="currentColor"
                    />
                  </div>
                </div>
              )}
            </footer>
          </div>
        )}
      </div>
    </div>
  );
}

/** A titled section on the paper: monospace number + uppercase label + rule, with an optional trailing action. */
export function PaperSection({
  sectionId,
  number,
  title,
  action,
  accentColor,
  allowBreak = false,
  children,
}: {
  sectionId?: string;
  number: string;
  title: string;
  action?: React.ReactNode;
  accentColor?: string;
  allowBreak?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      id={sectionId ? `visit-section-${sectionId}` : undefined}
      className={`${allowBreak ? '' : 'rx-no-break '}scroll-mt-24`}
    >
      <div className="mb-3 flex items-center gap-2.5">
        <span
          className="font-mono text-11 font-semibold"
          style={{ color: accentColor ?? 'var(--primary)' }}
        >
          {number}
        </span>
        <span
          className="text-12 font-bold uppercase tracking-[0.13em]"
          style={{ color: accentColor ?? 'var(--primary-hover)' }}
        >
          {title}
        </span>
        <span className="h-px flex-1 bg-border" />
        {action}
      </div>
      {children}
    </section>
  );
}
